/**
 * Canonical HTTP client shared by every Vollgas web app.
 *
 * Hoisted from apps/management + apps/technical, which had drifted
 * copies (the technical one parsed a non-existent `message` field
 * instead of RFC 7807 `detail`, and missed `application/problem+json`).
 * apps/web and apps/admin joined in 2026-08 — they had kept hand-rolled
 * copies that drifted the same way, web still parsing `message` and so
 * showing blank error toasts for every backend problem response.
 * One implementation so the auth surface + error handling can't drift
 * again.
 *
 * Responsibilities (per docs/design/14-security.md):
 * - Always send cookies (`credentials: 'include'`) so the HttpOnly
 *   `vg_session` cookie rides along automatically.
 * - On every state-mutating request, attach `X-CSRF-Token` (double-submit
 *   pattern). See {@link getCsrfToken} for where the value comes from —
 *   the `vg_csrf` cookie is only readable when the app and the API share
 *   a registrable domain, which they do not in production.
 * - On `401 token-expired`, slide the session once via
 *   `/users/auth/refresh` and replay. A 5s cooldown prevents loops.
 * - Errors surface as {@link ApiError} parsed from RFC 7807 Problem
 *   Details (`detail` / `title` / `type`).
 *
 * No bearer tokens or local secrets — auth is fully cookie-based.
 */
import { saveBlobAsFile } from '../utils/download'

// Read defensively so this file typechecks standalone (packages/shared
// doesn't pull in vite/client types); each app's build injects the real
// value at bundle time. Read per call rather than once at load: in a
// bundle the value is a compile-time constant either way, and in Vitest
// a `vi.stubEnv` must be visible to a module that was already imported —
// `vi.resetModules` does not re-run a workspace-linked package.
function baseUrl(): string {
  return (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ?? ''
}

const DEV: boolean = (import.meta as { env?: { DEV?: boolean } }).env?.DEV ?? false

const REFRESH_COOLDOWN_MS = 5_000
let lastRefreshAttempt = 0

let onUnauthorized: (() => void) | null = null

/**
 * Register what should happen when a request comes back 401 and the
 * refresh could not save it — typically "drop the cached profile".
 *
 * The client cannot own that itself: what "signed out" means is the
 * app's business (apps/web caches a user object; the staff apps read
 * the session on demand). Registering it here rather than each app
 * re-implementing `request` is what lets all four share one client.
 *
 * Call it once, at the module that re-exports this client, so the wiring
 * exists for anything that imports the client — tests included.
 */
export function setOnUnauthorized(fn: (() => void) | null): void {
  onUnauthorized = fn
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly body: unknown
  /**
   * Whole seconds from a `Retry-After` header (429 / 503), when the server
   * sent one in delta-seconds form. Absent otherwise — an HTTP-date value
   * is deliberately not parsed; callers fall back to generic copy.
   */
  readonly retryAfterSeconds?: number
  constructor(
    status: number,
    message: string,
    body: unknown,
    code: string = 'unknown',
    retryAfterSeconds?: number,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  json?: unknown
  /**
   * Multipart body for file uploads. Mutually exclusive with `json`.
   *
   * `Content-Type` is deliberately NOT set: the browser has to write it
   * itself so it can append the multipart boundary. Setting it by hand
   * produces a request the server cannot parse, which is the classic
   * FormData bug.
   */
  formData?: FormData
  /** Skip the 401 → refresh retry (used by the refresh call itself). */
  skipRefresh?: boolean
  /**
   * Skip the `/users/me` probe that would otherwise run when a mutation
   * finds no cached CSRF token. Internal — set by the refresh call, for
   * which the probe is circular: `/users/me` either 401s (so refresh will
   * too) or succeeds and slides the session behind the client's back,
   * making the refresh it was preparing for redundant.
   */
  skipCsrfBootstrap?: boolean
  /**
   * `false` marks a public route: skip the 401 → refresh retry, because
   * there is no session to slide and a refresh would only add a wasted
   * round-trip before the real error surfaces.
   *
   * It does **not** mean "this browser has no session", and in
   * particular it does not suppress the CSRF header — a signed-in
   * customer booking as a guest still carries the cookie, and the
   * backend enforces CSRF on any request that arrives with one.
   */
  auth?: boolean
}

/**
 * Turn a failed response into an {@link ApiError} carrying what the server
 * actually said. The backend answers every error as RFC 7807 Problem
 * Details, so `detail` is a sentence written for the operator ("Only a
 * COMPLETED work order can be invoiced.") — far better than the status
 * code. `fallback` covers a non-JSON or empty body.
 *
 * Shared by the JSON and the binary paths: the binary ones used to build
 * a bare "Download failed (404)" and throw the body away, so a PDF that
 * failed for a nameable reason showed the user nothing but a number.
 */
async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-json / binary / empty body */
  }
  const problem = body as { detail?: string; title?: string; type?: string } | null
  const message = problem?.detail ?? problem?.title ?? fallback
  const code = problem?.type ?? res.headers.get('X-Error-Code') ?? 'unknown'
  return new ApiError(res.status, message, body, code, parseRetryAfter(res.headers.get('Retry-After')))
}

/** `Retry-After: 120` → 120. HTTP-dates and garbage → undefined. */
function parseRetryAfter(value: string | null): number | undefined {
  if (value === null || !/^\d+$/.test(value.trim())) return undefined
  return Number(value.trim())
}

/**
 * Any media type that carries JSON, not just the two we used to hardcode.
 *
 * RFC 6839 lets a media type declare JSON structure with a `+json` suffix,
 * which is how `application/problem+json`, `application/hal+json` and
 * `application/vnd.spring-boot.actuator.v3+json` all announce themselves.
 * Matching the literal strings `application/json` and
 * `application/problem+json` missed every vendor type, and the fallthrough
 * is not an error — it returns `res.text()` cast to `T`, so the caller gets
 * a raw string typed as its response object and only finds out when a field
 * reads `undefined`. That is exactly what happened to the admin dashboard's
 * backend-health card: Actuator answers with
 * `application/vnd.spring-boot.actuator.v3+json`, so `health.data` was the
 * unparsed JSON text and `health.data.status` rendered as nothing.
 *
 * Parameters (`; charset=utf-8`) are stripped and the comparison is
 * lowercased — the header permits both. `application/jsonp` deliberately
 * does not match: the suffix rule is anchored.
 */
const JSON_MEDIA_TYPE = /^application\/([\w.-]+\+)?json$/

function isJsonMediaType(contentType: string | null): boolean {
  if (!contentType) return false
  const essence = contentType.split(';')[0]?.trim().toLowerCase() ?? ''
  return JSON_MEDIA_TYPE.test(essence)
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const target = `${name}=`
  const parts = document.cookie.split('; ')
  for (const part of parts) {
    if (part.startsWith(target)) return decodeURIComponent(part.slice(target.length))
  }
  return null
}

/**
 * In-memory CSRF token. Deliberately not persisted to localStorage or
 * sessionStorage: `GET /users/me` re-supplies it on every app load, so
 * there is nothing to gain from leaving copies on disk.
 */
let csrfToken: string | null = null

/** In-flight `/users/me` bootstrap, so a burst of mutations shares one. */
let csrfBootstrap: Promise<void> | null = null

/**
 * Pull `csrfToken` out of any response body that carries one.
 *
 * The backend populates it on login, magic-link redeem, `/users/me` and
 * `/users/auth/refresh`. Absorbing it here rather than in each app's auth
 * module means all four apps get it without wiring, and a future auth
 * entry point can't forget to.
 */
function absorbCsrf(body: unknown): void {
  if (body && typeof body === 'object' && 'csrfToken' in body) {
    const token = (body as { csrfToken?: unknown }).csrfToken
    if (typeof token === 'string' && token.length > 0) csrfToken = token
  }
}

/**
 * The token to echo in `X-CSRF-Token`.
 *
 * Memory first, cookie second. The cookie path only works when the app
 * and the API are same-site (local dev, and production once the API moves
 * under `vollgaskfz.at`); in the `*.web.app` -> `*.run.app` deployment the
 * cookie is sent but not readable, so the response-body value is the only
 * one available.
 */
export function getCsrfToken(): string | null {
  return csrfToken ?? readCookie('vg_csrf')
}

/**
 * Async form of {@link getCsrfToken} that will bootstrap if nothing is
 * cached yet.
 *
 * For the handful of call sites that must bypass {@link request} — file
 * uploads and multipart posts, whose bodies this client does not model —
 * so they get the same guarantee as everything going through `api.*`
 * instead of silently sending no header.
 */
export async function ensureCsrfToken(): Promise<string | null> {
  const cached = getCsrfToken()
  if (cached) return cached
  await bootstrapCsrf()
  return getCsrfToken()
}

/** Drop the cached token — call on sign-out. */
export function clearCsrfToken(): void {
  csrfToken = null
  csrfBootstrap = null
}

/**
 * Last-resort bootstrap: fetch `/users/me` purely to obtain a token.
 *
 * Every app already probes `/users/me` on mount, so this rarely fires —
 * it exists so a mutation that somehow runs first still succeeds instead
 * of 403ing. The probe is a GET, so it cannot recurse into this path.
 */
async function bootstrapCsrf(): Promise<void> {
  if (!csrfBootstrap) {
    csrfBootstrap = (async () => {
      try {
        const res = await rawFetch('/users/me', { method: 'GET' })
        if (res.ok) absorbCsrf(await res.json())
      } catch {
        /* offline / no session — the request proceeds and fails on its own terms */
      }
    })().finally(() => {
      csrfBootstrap = null
    })
  }
  return csrfBootstrap
}

function isLogoutPath(path: string): boolean {
  return path === '/users/auth/logout' || path.startsWith('/users/auth/logout?')
}

function isMutating(method?: string): boolean {
  if (!method) return false
  const m = method.toUpperCase()
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE'
}

/**
 * The language the API should answer in.
 *
 * Set by each app from the language the user picked in the UI. Deliberately
 * not left to the browser's own `Accept-Language`: a manager on an
 * English-language macOS who has switched the app to German wants German
 * errors, and the browser header would say otherwise.
 *
 * Unset until an app calls {@link setApiLanguage}, in which case the server
 * applies its own default — German, the shop's language.
 */
let apiLanguage: string | null = null

/** @see apiLanguage */
export function setApiLanguage(lang: string | null): void {
  apiLanguage = lang
}

async function rawFetch(path: string, opts: RequestOptions): Promise<Response> {
  const headers = new Headers(opts.headers)
  let body: BodyInit | undefined

  // Error messages come back translated; the server keys them off the
  // problem slug, which does not change with the language.
  if (apiLanguage && !headers.has('Accept-Language')) {
    headers.set('Accept-Language', apiLanguage)
  }

  if (opts.json !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(opts.json)
  } else if (opts.formData !== undefined) {
    // No Content-Type header — see RequestOptions.formData.
    body = opts.formData
  }

  if (isMutating(opts.method) && !headers.has('X-CSRF-Token')) {
    let csrf = getCsrfToken()
    // A known token is always sent, `auth: false` included — a signed-in
    // customer booking as a guest still carries the session cookie, and
    // the backend enforces CSRF on anything that arrives with one. What
    // `auth: false` does suppress is the *probe*: a public route is by
    // definition reachable without a session, so paying a guaranteed-401
    // round-trip in front of every login and contact-form POST buys
    // nothing. Signed-in users have a token cached either way, because
    // every app loads `/users/me` before rendering anything that mutates.
    if (!csrf && opts.auth !== false && !opts.skipCsrfBootstrap) {
      await bootstrapCsrf()
      csrf = getCsrfToken()
    }
    if (csrf) headers.set('X-CSRF-Token', csrf)
  }

  // Dev-only network throttle — set `window.__vollgas_devSlow = 2000` in
  // the console to inject a delay and exercise loading states. Came from
  // apps/admin's client; kept here so migrating it to the shared one
  // didn't quietly take the tool away.
  if (DEV) {
    const slow = (globalThis as { __vollgas_devSlow?: number }).__vollgas_devSlow
    if (typeof slow === 'number' && slow > 0) {
      await new Promise((r) => setTimeout(r, slow))
    }
  }

  return fetch(apiUrl(path), {
    ...opts,
    headers,
    body,
    credentials: 'include',
  })
}

/**
 * Slide the session's idle window. The server re-issues `vg_session` via
 * Set-Cookie, so success is simply a 2xx — there is no token in the body.
 *
 * Goes through {@link rawFetch} rather than a bare `fetch` because this
 * is a POST carrying the session cookie, so `CsrfDoubleSubmitFilter`
 * demands the `X-CSRF-Token` echo like any other mutating call. A bare
 * fetch here fails in precisely the case the refresh exists for: the
 * filter waves through requests whose session is already dead (the auth
 * layer rejects those anyway), so the header is only load-bearing when
 * the session is still valid — which is exactly when refreshing would
 * have worked.
 */
async function tryRefresh(): Promise<boolean> {
  const now = Date.now()
  if (now - lastRefreshAttempt < REFRESH_COOLDOWN_MS) return false
  lastRefreshAttempt = now

  try {
    const res = await rawFetch('/users/auth/refresh', { method: 'POST', skipCsrfBootstrap: true })
    // Body carries the session's CSRF token — and a rotated one when the
    // cookie had gone stale, which is precisely when the replayed request
    // would otherwise 403 right after surviving the 401.
    if (res.ok) {
      try {
        absorbCsrf(await res.clone().json())
      } catch {
        /* 204 / non-JSON — nothing to absorb */
      }
    }
    return res.ok
  } catch {
    return false
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await rawFetch(path, opts)

  // Centralised rather than left to each app's logout handler: all four
  // have their own, and a missed one would leave a dead token cached.
  // Returns before the body is parsed, so a token in the response can't
  // re-populate the cache we just dropped. Logout answers 204 — every
  // caller types it `api.post<void>`.
  // Exact match, not startsWith: `/users/auth/logout-all` also starts with
  // `/users/auth/logout`, and treating it as a sign-out both threw its
  // response body away and dropped a CSRF token that is still valid — the
  // current session deliberately survives a logout-all.
  if (isLogoutPath(path) && res.ok) {
    clearCsrfToken()
    return undefined as T
  }

  if (res.status === 401 && opts.auth !== false && !opts.skipRefresh) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      res = await rawFetch(path, opts)
    }
  }

  if (res.status === 401) {
    // The session is gone, so the token cached against it is worthless —
    // and worse, keeping it means the next sign-in would send a stale
    // header until /users/me overwrote it.
    clearCsrfToken()
    onUnauthorized?.()
    throw new ApiError(401, 'Unauthorized', null, 'unauthorized')
  }

  if (!res.ok) {
    throw await toApiError(res, `Request failed (${res.status})`)
  }

  if (res.status === 204) return undefined as T
  if (isJsonMediaType(res.headers.get('content-type'))) {
    const body = (await res.json()) as T
    absorbCsrf(body)
    return body
  }
  return (await res.text()) as unknown as T
}

/**
 * Fetch a binary document as a Blob (same auth, CSRF and 401→refresh
 * handling as {@link request}) — e.g. a rendered PDF preview. A POST with
 * a `json` body works too: the previews render from unsaved form state.
 *
 * <p>A Blob rather than an object URL so it can sit in a query cache: a
 * URL is a handle that has to be revoked, and a cached one outlives the
 * component that would revoke it.
 */
export async function fetchFileBlob(path: string, opts: RequestOptions = { method: 'GET' }): Promise<Blob> {
  let res = await rawFetch(path, opts)
  if (res.status === 401) {
    const refreshed = await tryRefresh()
    if (refreshed) res = await rawFetch(path, opts)
  }
  if (!res.ok) {
    throw await toApiError(res, `Load failed (${res.status})`)
  }
  return res.blob()
}

/**
 * Fetch a binary document into an object URL (same auth + 401→refresh
 * handling as {@link request}) for in-app viewing — e.g. a PDF in an
 * iframe. The caller owns the URL and must `URL.revokeObjectURL` it.
 */
export async function fetchFileBlobUrl(path: string): Promise<string> {
  return URL.createObjectURL(await fetchFileBlob(path))
}

/**
 * Download a binary document (e.g. an invoice PDF) with the same auth +
 * 401→refresh handling as {@link request}, then trigger a browser save.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let res = await rawFetch(path, { method: 'GET' })
  if (res.status === 401) {
    const refreshed = await tryRefresh()
    if (refreshed) res = await rawFetch(path, { method: 'GET' })
  }
  if (!res.ok) {
    throw await toApiError(res, `Download failed (${res.status})`)
  }
  saveBlobAsFile(await res.blob(), fallbackName)
}

/**
 * Sign out, for real.
 *
 * <p>The four apps used to call `POST /users/auth/logout` and swallow any
 * rejection, clearing their local state regardless. That turned the
 * production CSRF failure into a silent lie: the button said "signed
 * out", the server session was untouched, and one refresh signed the user
 * back in. A logout that fails must say so.
 *
 * One retry, because the single likeliest failure is a stale CSRF token —
 * recoverable by re-fetching it. A 401 means the session is already gone,
 * which is the outcome logout wanted, so it counts as success. Anything
 * still failing after that throws, and the caller must surface it rather
 * than pretend.
 */
export async function logout(): Promise<void> {
  const attempt = () => request<void>('/users/auth/logout', { method: 'POST' })
  try {
    await attempt()
    return
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      clearCsrfToken()
      return
    }
    clearCsrfToken()
    await bootstrapCsrf()
    await attempt()
  }
}

/**
 * Sign out every *other* session — lost device, shared machine, suspected
 * breach. The current session deliberately survives; call {@link logout}
 * after it to end this one too.
 */
export async function logoutEverywhereElse(): Promise<{ revokedCount: number }> {
  return request<{ revokedCount: number }>('/users/auth/logout-all', { method: 'POST' })
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, json?: unknown, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'POST', json }),
  put: <T>(path: string, json?: unknown, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'PUT', json }),
  patch: <T>(path: string, json?: unknown, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'PATCH', json }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
}

/**
 * The absolute URL the API serves `path` at — for the few places that hand
 * a URL to the browser instead of fetching it through {@link request}: an
 * `<a href>` download, an `<iframe src>`.
 *
 * Everything that goes through `request` is prefixed inside {@link rawFetch}
 * and never needs this. A bare `/me/invoices/47/pdf` in an `href`, by
 * contrast, resolves against the APP's origin: fine in dev, where the Vite
 * proxy forwards it, and a 404 in production, where the app is on
 * vollgaskfz.at, the API on api.vollgaskfz.at, and Firebase Hosting rewrites
 * every unknown path to index.html.
 *
 * The session cookie still rides on such a navigation: production scopes it
 * to `.vollgaskfz.at` with `SameSite=Lax`, and a top-level GET to a
 * same-site host carries a Lax cookie. In dev `VITE_API_BASE_URL` is empty
 * and the path comes back unchanged, so the proxy keeps working.
 */
export function apiUrl(path: string): string {
  return `${baseUrl()}${path}`
}

/**
 * Legacy alias kept for feature modules that still call `simulate(() => …)`.
 * Returns the promise unchanged after a tiny delay. Drop a module's
 * `simulate` import once it's wired to real endpoints.
 */
export async function simulate<T>(fn: () => T, latencyMs = 100): Promise<T> {
  await new Promise((r) => setTimeout(r, latencyMs))
  return fn()
}
